import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Save, 
  Plus, 
  Trash2, 
  ShoppingCart, 
  User, 
  Phone, 
  MapPin, 
  Calendar, 
  Search,
  UserCheck,
  Package,
  DollarSign,
  Clock,
  ArrowLeft,
  ChevronDown
} from 'lucide-react';
import { Order, Make, Model, ServiceType, Technician, CartItem, OrderStatusUpdateRequest } from '../types';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const CreateOrderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  // Form state
  const [orderType, setOrderType] = useState('Online');
  const [customerName, setCustomerName] = useState('');
  const [customerMobile, setCustomerMobile] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState('COD');
  const [orderStatus, setOrderStatus] = useState('Created');

  // Selection state
  const [makes, setMakes] = useState<Make[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [services, setServices] = useState<ServiceType[]>([]);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  
  const [selectedMake, setSelectedMake] = useState<number | null>(null);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [selectedTechnician, setSelectedTechnician] = useState<number | null>(null);
  
  // Cart state
  const [cart, setCart] = useState<CartItem[]>([]);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [editableServiceCost, setEditableServiceCost] = useState<number | null>(null);
  
  // Order totals state (for edit mode)
  const [orderTotals, setOrderTotals] = useState<{
    totalItemPrice: number;
    totalServiceCost: number;
    gstAmount: number;
    finalAmount: number;
  } | null>(null);
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(false);
  
  // Search state for autocomplete
  const [makeSearch, setMakeSearch] = useState('');
  const [modelSearch, setModelSearch] = useState('');
  const [showMakeDropdown, setShowMakeDropdown] = useState(false);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [filteredMakes, setFilteredMakes] = useState<Make[]>([]);
  const [filteredModels, setFilteredModels] = useState<Model[]>([]);

  useEffect(() => {
    loadMakes();
    loadTechnicians();
    if (isEditMode && id) {
      loadOrderDetails(parseInt(id));
    }
  }, [isEditMode, id]);

  // Filter makes based on search
  useEffect(() => {
    if (makeSearch) {
      const filtered = makes.filter(make => 
        make.name.toLowerCase().includes(makeSearch.toLowerCase())
      );
      setFilteredMakes(filtered);
    } else {
      setFilteredMakes(makes);
    }
  }, [makes, makeSearch]);

  // Filter models based on search
  useEffect(() => {
    if (modelSearch) {
      const filtered = models.filter(model => 
        model.name.toLowerCase().includes(modelSearch.toLowerCase())
      );
      setFilteredModels(filtered);
    } else {
      setFilteredModels(models);
    }
  }, [models, modelSearch]);

  const handleMakeSelect = (make: Make) => {
    setSelectedMake(make.id);
    setMakeSearch(make.name);
    setShowMakeDropdown(false);
    // Clear model selection
    setSelectedModel(null);
    setModelSearch('');
    setServices([]);
    setCart([]);
    // Small delay to ensure state is updated before loading models
    setTimeout(() => {
      loadModels(make.id);
    }, 0);
  };

  const handleModelSelect = (model: Model) => {
    setSelectedModel(model);
    setModelSearch(model.name);
    setShowModelDropdown(false);
    // Small delay to ensure state is updated before loading services
    setTimeout(() => {
      loadServices(model.id);
    }, 0);
  };



  const loadMakes = async () => {
    try {
      const response = await apiService.getMakes();
      if (response.isSuccess) {
        setMakes(response.entity || []);
        setFilteredMakes(response.entity || []);
      } else {
        toast.error('Failed to load makes');
      }
    } catch (error) {
      console.error('Error loading makes:', error);
      toast.error('Error loading makes');
    }
  };

  const loadModels = async (makeId: number) => {
    try {
      // Clear previous selections
      setSelectedModel(null);
      setServices([]);
      setCart([]);
      
      const response = await apiService.getModels(makeId);
      if (response.isSuccess) {
        setModels(response.entity || []);
        setFilteredModels(response.entity || []);
      } else {
        toast.error('Failed to load models');
      }
    } catch (error) {
      console.error('Error loading models:', error);
      toast.error('Error loading models');
    }
  };

  const loadServices = async (modelId: number) => {
    try {
      // Clear previous cart
      setCart([]);
      
      const response = await apiService.getServiceTypes(modelId);
      if (response.isSuccess) {
        setServices(response.entity || []);
      } else {
        toast.error('Failed to load services');
      }
    } catch (error) {
      console.error('Error loading services:', error);
      toast.error('Error loading services');
    }
  };

  const loadTechnicians = async () => {
    try {
      const response = await apiService.getTechnicians();
      if (response.isSuccess) {
        setTechnicians(response.entity || []);
      }
    } catch (error) {
      console.error('Error loading technicians:', error);
    }
  };

  const loadOrderDetails = async (orderId: number) => {
    setLoadingOrder(true);
    try {
      const response = await apiService.getOrder(orderId);
      if (response.isSuccess && response.entity) {
        const order = response.entity;
        // Handle both orderType and OrderType property names from API
        const orderTypeValue = (order as any).OrderType ?? (order as any).orderType ?? 'Online';
        setOrderType(orderTypeValue);
        setCustomerName(order.customerName);
        setCustomerMobile(order.customerMobile);
        setCustomerAddress(order.customerAddress);
        setOrderDate(order.orderDate.toString().split('T')[0]);
        setPaymentMethod(order.paymentMethod);
        setOrderStatus(order.orderStatus);
        setSelectedMake(order.makeId);
        setSelectedTechnician(order.technicianId);
        setDiscountAmount(order.discountAmount);
        
        // Set order totals from API response
        setOrderTotals({
          totalItemPrice: order.totalItemPrice || 0,
          totalServiceCost: order.totalServiceCost || 0,
          gstAmount: order.gstAmount || 0,
          finalAmount: order.finalAmount || 0
        });
        // Initialize editable service cost with the order's service cost
        setEditableServiceCost(order.totalServiceCost || 0);
        
        // Set make search field
        const selectedMakeObj = makes.find(m => m.id === order.makeId);
        if (selectedMakeObj) {
          setMakeSearch(selectedMakeObj.name);
        }
        
        // Set the selected model object after loading models
        if (order.makeId && order.modelId) {
          // Load models for the selected make and then set the selected model
          const modelsResponse = await apiService.getModels(order.makeId);
          if (modelsResponse.isSuccess) {
            setModels(modelsResponse.entity || []);
            setFilteredModels(modelsResponse.entity || []);
            const model = modelsResponse.entity?.find(m => m.id === order.modelId);
            if (model) {
              setSelectedModel(model);
              setModelSearch(model.name);
              
              // Load services for the model
              const servicesResponse = await apiService.getServiceTypes(order.modelId);
              if (servicesResponse.isSuccess) {
                setServices(servicesResponse.entity || []);
              }
            }
          }
        }
        
        // Load cart items from order
        if (order.orderItems) {
          const cartItems = order.orderItems.map(item => ({
            id: item.serviceId,
            serviceName: item.serviceName,
            actualPrice: item.actualPrice,
            serviceCost: item.serviceCost,
            quantity: item.quantity,
            // Add required ServiceType properties with defaults
            serviceType: 'Repair',
            warrantyPeriod: item.warrantyPeriod || 90,
            modelId: order.modelId,
            serviceImage: '',
            serviceDuration: 60,
            markedPrice: item.actualPrice.toString(),
            makeId: order.makeId,
            makeName: order.makeName,
            modelName: order.modelName,
            warrantyCoveredItems: item.warrantyCoveredItems || '',
            warrantyNotCoveredItems: item.warrantyNotCoveredItems || '',
            warrantyTypeDescription: item.warrantyTypeDescription || '',
            warrantyName: item.warrantyName || '',
            warrantyTypeId: item.warrantyTypeId || 1,
            warrantyEndDate: item.warrantyEndDate || new Date()
          }));
          setCart(cartItems as CartItem[]);
        }
      }
    } catch (error) {
      console.error('Error loading order:', error);
      toast.error('Error loading order details');
    } finally {
      setLoadingOrder(false);
    }
  };

  const addToCart = (service: ServiceType) => {
    const existingItem = cart.find(item => item.id === service.id);
    if (existingItem) {
      // Remove from cart if already added (toggle behavior like Angular)
      removeFromCart(service.id);
      return;
    }
    setCart([...cart, { ...service, quantity: 1 }]);
    // Reset editable service cost when cart changes in create mode
    if (!isEditMode) {
      setEditableServiceCost(null);
    }
    toast.success('Service added to cart');
  };

  const removeFromCart = (serviceId: number) => {
    setCart(cart.filter(item => item.id !== serviceId));
    // Reset editable service cost when cart changes in create mode
    if (!isEditMode) {
      setEditableServiceCost(null);
    }
    toast.success('Service removed from cart');
  };

  const updateQuantity = (serviceId: number, quantity: number) => {
    if (quantity < 1) return;
    setCart(cart.map(item => 
      item.id === serviceId ? { ...item, quantity } : item
    ));
    // Reset editable service cost when cart changes in create mode
    if (!isEditMode) {
      setEditableServiceCost(null);
    }
  };

  const calculateTotals = () => {
    // Calculate base totals
    let totalItemPrice: number;
    let totalServiceCost: number;
    
    // In edit mode, use the original order totals if available
    if (isEditMode && orderTotals) {
      totalItemPrice = orderTotals.totalItemPrice;
      // Use editable service cost if set, otherwise use original
      totalServiceCost = editableServiceCost !== null ? editableServiceCost : orderTotals.totalServiceCost;
    } else {
      // In create mode, calculate from cart items
      totalItemPrice = cart.reduce((sum, item) => sum + (item.actualPrice * item.quantity), 0);
      // Use editable service cost if set, otherwise calculate from cart
      if (editableServiceCost !== null) {
        totalServiceCost = editableServiceCost;
      } else {
        totalServiceCost = cart.reduce((sum, item) => sum + (item.serviceCost * item.quantity), 0);
      }
    }
    
    // Calculate GST on service cost only (not on item/part price)
    const gstAmount = (totalServiceCost * 18) / 100;

    // Calculate total with GST
    const totalWithGst = totalItemPrice + totalServiceCost + gstAmount;
    
    // Apply discount to final amount (after GST)
    const finalAmount = Math.max(totalWithGst - discountAmount, 0);
    
    return {
      totalItemPrice,
      totalServiceCost,
      gstAmount,
      finalAmount
    };
  };

  const validateForm = () => {
    if (!customerName.trim()) {
      toast.error('Customer name is required');
      return false;
    }
    if (!customerMobile.trim() || customerMobile.length !== 10) {
      toast.error('Valid 10-digit mobile number is required');
      return false;
    }
    if (!customerAddress.trim()) {
      toast.error('Customer address is required');
      return false;
    }
    if (!selectedMake) {
      toast.error('Make selection is required');
      return false;
    }
    if (!selectedModel) {
      toast.error('Model selection is required');
      return false;
    }
    if (cart.length === 0) {
      toast.error('At least one service must be selected');
      return false;
    }
    if (!paymentMethod) {
      toast.error('Payment method is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const totals = calculateTotals();
      
      // Convert cart items to orderItems format
      const orderItems = cart.map((item) => ({
        id: 0, // Will be set by backend
        orderId: 0, // Will be set by backend
        serviceId: item.id,
        serviceName: item.serviceName,
        serviceType: item.serviceType || 'Repair',
        actualPrice: item.actualPrice,
        serviceCost: item.serviceCost,
        quantity: item.quantity,
        itemTotal: item.actualPrice * item.quantity,
        serviceStatus: 'Pending',
        notes: '',
        warrantyPeriod: item.warrantyPeriod || 90,
        warrantyEndDate: item.warrantyEndDate || new Date(Date.now() + (item.warrantyPeriod || 90) * 24 * 60 * 60 * 1000),
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: 1,
        warrantyTypeId: item.warrantyTypeId || 1,
        warrantyName: item.warrantyName || '',
        warrantyTypeDescription: item.warrantyTypeDescription || '',
        warrantyCoveredItems: item.warrantyCoveredItems || '',
        warrantyNotCoveredItems: item.warrantyNotCoveredItems || '',
        serviceImage: item.serviceImage || ''
      }));
      
      const orderData: Partial<Order> = {
        customerName,
        customerMobile,
        customerEmail: '', // Add email field if needed
        customerAddress,
        makeId: selectedMake!,
        makeName: makes.find(m => m.id === selectedMake)?.name || '',
        modelId: selectedModel!.id,
        modelName: selectedModel!.name,
        orderDate: new Date(orderDate),
        orderStatus: isEditMode ? orderStatus : 'Created',
        OrderType: orderType,
        totalItemPrice: totals.totalItemPrice,
        totalServiceCost: totals.totalServiceCost,
        discountAmount,
        gstPercentage: 18,
        gstAmount: totals.gstAmount,
        finalAmount: totals.finalAmount,
        paymentStatus: 'Pending',
        paymentMethod,
        amountPaid: 0,
        balanceAmount: totals.finalAmount,
        technicianId: selectedTechnician || 0,
        notes: '',
        deviceCondition: 'Good',
        warrantyPeriod: 90,
        warrantyEndDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000),
        createdBy: 1,
        updatedBy: 1,
        isActive: true,
        orderItems: orderItems
      };

      let response;
      if (isEditMode && id) {
        response = await apiService.updateOrder(parseInt(id), orderData as Order);
      } else {
        response = await apiService.createOrder(orderData as Order);
      }

      if (response.isSuccess) {
        toast.success(`Order ${isEditMode ? 'updated' : 'created'} successfully`);
        navigate('/orders');
      } else {
        toast.error(response.errors?.join(', ') || `Failed to ${isEditMode ? 'update' : 'create'} order`);
      }
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error('Error saving order');
    } finally {
      setLoading(false);
    }
  };

  const assignTechnician = async () => {
    if (!selectedTechnician || !id) return;
    
    try {
      const response = await apiService.assignTechnicianToOrder(parseInt(id), selectedTechnician);
      if (response.isSuccess) {
        toast.success('Technician assigned successfully');
        setOrderStatus('Assigned'); // Update status automatically
      } else {
        toast.error('Failed to assign technician');
      }
    } catch (error) {
      console.error('Error assigning technician:', error);
      toast.error('Error assigning technician');
    }
  };

  const updateOrderStatus = async () => {
    if (!id || !orderStatus) return;
    
    try {
      const request: OrderStatusUpdateRequest = {
        status: orderStatus,
        notes: '',
        userId: 1
      };

      const response = await apiService.updateOrderStatus(parseInt(id), request);
      if (response.isSuccess) {
        toast.success('Order status updated successfully');
      } else {
        toast.error(response.errors?.join(', ') || 'Failed to update order status');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Error updating order status');
    }
  };

  const totals = calculateTotals();

  if (loadingOrder) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/orders')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <ShoppingCart className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                {isEditMode ? 'Edit Order' : 'Create New Order'}
              </h1>
              {isEditMode && <p className="text-sm text-gray-500">Order ID: {id}</p>}
            </div>
          </div>
        </div>
        
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50"
        >
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {isEditMode ? 'Update Order' : 'Create Order'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Customer & Product Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Information */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <User className="h-4 w-4 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Customer Information</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Order Type</label>
                <select
                  value={orderType}
                  onChange={(e) => setOrderType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Online">Online</option>
                  <option value="Offline">Offline</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Customer Name</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Enter customer name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number</label>
                <input
                  type="tel"
                  value={customerMobile}
                  onChange={(e) => setCustomerMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Enter 10-digit mobile number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <textarea
                  value={customerAddress}
                  onChange={(e) => setCustomerAddress(e.target.value)}
                  placeholder="Enter customer address"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Order Date</label>
                <input
                  type="date"
                  value={orderDate}
                  onChange={(e) => setOrderDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {!isEditMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="COD">Cash on Delivery (COD)</option>
                    <option value="Online">Online Payment</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Device Information */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <Package className="h-4 w-4 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Device Information</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Make</label>
                <div className="relative">
                  <input
                    type="text"
                    value={makeSearch}
                    onChange={(e) => {
                      setMakeSearch(e.target.value);
                      setShowMakeDropdown(true);
                    }}
                    onFocus={() => setShowMakeDropdown(true)}
                    onClick={() => {
                      setShowMakeDropdown(true);
                      // Show all makes when clicked, not just filtered
                      if (!makeSearch) {
                        setFilteredMakes(makes);
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowMakeDropdown(false), 300)}
                    placeholder="Search and select make..."
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </div>
                  {showMakeDropdown && filteredMakes.length > 0 && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredMakes.map((make) => (
                        <button
                          key={make.id}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleMakeSelect(make);
                          }}
                          className="w-full px-3 py-2 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 focus:outline-none focus:bg-blue-50"
                        >
                          <div className="flex items-center space-x-3">
                            {make.imagePath && (
                              <img
                                src={apiService.getFullImageUrl(make.imagePath)}
                                alt={make.name}
                                className="w-6 h-6 rounded object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                }}
                              />
                            )}
                            <span className="font-medium">{make.name}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                <div className="relative">
                  <input
                    type="text"
                    value={modelSearch}
                    onChange={(e) => {
                      setModelSearch(e.target.value);
                      setShowModelDropdown(true);
                    }}
                    onFocus={() => setShowModelDropdown(true)}
                    onClick={() => {
                      if (selectedMake) {
                        setShowModelDropdown(true);
                        // Show all models when clicked, not just filtered
                        if (!modelSearch) {
                          setFilteredModels(models);
                        }
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowModelDropdown(false), 300)}
                    placeholder="Search and select model..."
                    disabled={!selectedMake}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className={`h-4 w-4 ${selectedMake ? 'text-gray-400' : 'text-gray-300'}`} />
                  </div>
                  {showModelDropdown && filteredModels.length > 0 && selectedMake && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredModels.map((model) => (
                        <button
                          key={model.id}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleModelSelect(model);
                          }}
                          className="w-full px-3 py-2 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 focus:outline-none focus:bg-blue-50"
                        >
                          <div className="flex items-center space-x-3">
                            {model.imagePath && (
                              <img
                                src={apiService.getFullImageUrl(model.imagePath)}
                                alt={model.name}
                                className="w-6 h-6 rounded object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                }}
                              />
                            )}
                            <span className="font-medium">{model.name}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Services Selection */}
          {selectedModel && (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="border-b border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Package className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Available Services</h3>
                      <p className="text-sm text-gray-500">Select services for {selectedModel.name}</p>
                    </div>
                  </div>
                  <div className="bg-blue-50 px-3 py-1 rounded-full">
                    <span className="text-sm font-medium text-blue-700">{services.length} available</span>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  {services.map((service) => {
                    const isInCart = cart.some(item => item.id === service.id);
                    return (
                      <div 
                        key={service.id} 
                        className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md ${
                          isInCart 
                            ? 'border-blue-500 bg-blue-50 shadow-sm' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => addToCart(service)}
                      >
                        {/* Service Image */}
                        <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-lg overflow-hidden">
                          {service.serviceImage ? (
                            <img
                              src={apiService.getFullImageUrl(service.serviceImage)}
                              alt={service.serviceName}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                target.nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                          ) : null}
                          <div className={`w-full h-full flex items-center justify-center ${service.serviceImage ? 'hidden' : ''}`}>
                            <Package className="h-6 w-6 text-gray-400" />
                          </div>
                        </div>
                        
                        {/* Service Details */}
                        <div className="ml-4 flex-1 min-w-0">
                          <h4 className="text-base font-semibold text-gray-900 truncate">
                            {service.serviceName}
                          </h4>
                          <div className="mt-1 flex items-center space-x-4">
                            <span className="text-lg font-bold text-blue-600">
                              ₹{service.actualPrice.toLocaleString()}
                            </span>
                            <span className="text-sm text-gray-500">
                              Service Cost: ₹{service.serviceCost.toLocaleString()}
                            </span>
                          </div>
                          <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                            {service.warrantyPeriod && (
                              <div className="flex items-center space-x-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                </svg>
                                <span>{service.warrantyPeriod} days warranty</span>
                              </div>
                            )}
                            {service.serviceDuration && (
                              <div className="flex items-center space-x-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                </svg>
                                <span>{service.serviceDuration} min</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Action Button */}
                        <div className="ml-4 flex-shrink-0">
                          {isInCart ? (
                            <div className="flex items-center space-x-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span className="text-sm font-medium">Added</span>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                              <Plus className="w-4 h-4" />
                              <span className="text-sm font-medium">Add</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {services.length === 0 && (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Package className="h-8 w-8 text-gray-400" />
                    </div>
                    <h4 className="text-lg font-medium text-gray-600 mb-2">No Services Available</h4>
                    <p className="text-gray-500">No services are currently available for this model</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Edit Mode Actions */}
          {isEditMode && (
            <>
              {/* Technician Assignment */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <UserCheck className="h-4 w-4 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Assign Technician</h3>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-1">
                    <select
                      value={selectedTechnician || ''}
                      onChange={(e) => setSelectedTechnician(e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Technician</option>
                      {technicians.map((tech) => (
                        <option key={tech.id} value={tech.id}>
                          {tech.name} - {tech.specialization}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={assignTechnician}
                    disabled={!selectedTechnician}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Assign
                  </button>
                </div>
              </div>

              {/* Order Status Update */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Clock className="h-4 w-4 text-amber-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Order Status</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={orderStatus}
                      onChange={(e) => setOrderStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="Created">Created</option>
                      <option value="Assigned">Assigned</option>
                      <option value="Service InProgress">Service InProgress</option>
                      <option value="Service Done">Service Done</option>
                      <option value="Payment Received">Payment Received</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                    <select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="COD">Cash on Delivery (COD)</option>
                      <option value="Online">Online Payment</option>
                    </select>
                  </div>
                </div>
                
                <button
                  onClick={updateOrderStatus}
                  disabled={!orderStatus}
                  className="mt-4 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Update Status
                </button>
              </div>
            </>
          )}
        </div>

        {/* Right Column - Cart & Summary */}
        <div className="space-y-6">
          {/* Shopping Cart */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <ShoppingCart className="h-4 w-4 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Service Cart</h3>
            </div>
            
            {cart.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No services selected</p>
            ) : (
              <div className="space-y-4">
                {cart.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{item.serviceName}</h4>
                      <p className="text-sm text-gray-500">₹{item.actualPrice.toLocaleString()}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                        className="w-16 px-2 py-1 border border-gray-300 rounded text-center"
                      />
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Order Summary */}
          {(cart.length > 0 || (isEditMode && orderTotals)) && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>Item Total</span>
                  <span>₹{(totals.totalItemPrice || 0).toLocaleString()}</span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Service Cost</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editableServiceCost !== null ? editableServiceCost : totals.totalServiceCost}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setEditableServiceCost(value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="flex justify-between">
                  <span>GST (18%)</span>
                  <span>₹{(totals.gstAmount || 0).toLocaleString()}</span>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Discount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={discountAmount === 0 ? '' : discountAmount}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || value === null || value === undefined) {
                        setDiscountAmount(0);
                      } else {
                        const numValue = parseFloat(value);
                        setDiscountAmount(isNaN(numValue) ? 0 : numValue);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div className="border-t pt-3">
                  <div className="flex justify-between font-semibold text-lg">
                    <span>Final Amount</span>
                    <span>₹{(totals.finalAmount || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateOrderPage; 